import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tag,
  Badge,
  Dropdown,
  Menu,
  Modal,
  Form,
  message,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Typography,
  Drawer,
  Avatar,
  List,
  Timeline,
  Upload,
  Divider,
  Alert,
  Tabs,
  Steps,
  Descriptions,
  Popconfirm,
  Radio
} from 'antd'
import {
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  BankOutlined,
  FileAddOutlined,
  HistoryOutlined,
  BarChartOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHardHat,
  faMedkit,
  faShieldAlt,
  faBalanceScale,
  faClipboardList,
  faFileInvoiceDollar,
  faMoneyBillWave,
  faCalendarAlt,
  faUser,
  faBuilding,
  faStethoscope,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faFileAlt,
  faChartLine,
  faUsers,
  faFilter,
  faDownload,
  faUpload
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
// Import our backend APIs instead of mock data
import {
  getCompensationClaims,
  createCompensationClaim,
  updateCompensationClaim,
  updateClaimStatus,
  deleteCompensationClaim,
  addClaimDocument,
  getClaimsStatsByType
} from '../../backend-integration/api'
import { formatCurrency, formatDate, formatDateTime, getStatusColor, generateFileNumber } from '../../utils/helpers'
import './CompensationFundManager.css'

const { Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select
const { TextArea } = Input
const { TabPane } = Tabs
const { Step } = Steps

const CompensationFundManager = () => {
  const [loading, setLoading] = useState(false)
  const [claimsData, setClaimsData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [searchText, setSearchText] = useState('')
  const [filterClaimType, setFilterClaimType] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterAssignee, setFilterAssignee] = useState('all')
  const [dateRange, setDateRange] = useState([])
  const [claimModalVisible, setClaimModalVisible] = useState(false)
  const [editingClaim, setEditingClaim] = useState(null)
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false)
  const [selectedClaim, setSelectedClaim] = useState(null)
  const [documentModalVisible, setDocumentModalVisible] = useState(false)
  const [dashboardStats, setDashboardStats] = useState({
    totalClaims: 0,
    totalAmount: 0,
    pendingClaims: 0,
    approvedClaims: 0,
    coidClaims: 0,
    femClaims: 0,
    sapsClaims: 0,
    rmaClaims: 0
  })
  const [form] = Form.useForm()
  // Pagination states
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)

  // Load initial data
  useEffect(() => {
    loadClaimsData()
    loadDashboardStats()
  }, [])

  // Reload data when filters change
  useEffect(() => {
    loadClaimsData()
  }, [searchText, filterClaimType, filterStatus, dateRange, page, pageSize])

  const loadClaimsData = async () => {
    setLoading(true)
    try {
      // Build filter object
      let filter = {}

      if (searchText) {
        filter.$or = [
          { patientName: { $regex: searchText, $options: 'i' } },
          { claimNumber: { $regex: searchText, $options: 'i' } },
          { fileNo: { $regex: searchText, $options: 'i' } },
          { employerName: { $regex: searchText, $options: 'i' } }
        ]
      }

      if (filterClaimType !== 'all') {
        filter.claimType = filterClaimType
      }

      if (filterStatus !== 'all') {
        filter.status = filterStatus
      }

      if (dateRange && dateRange.length === 2) {
        filter.dateSubmitted = {
          $gte: dateRange[0].format('YYYY-MM-DD'),
          $lte: dateRange[1].format('YYYY-MM-DD')
        }
      }

      // Fetch data from API
      const response = await getCompensationClaims(filter, page, pageSize, '', { dateSubmitted: -1 })

      if (response && response.data) {
        setClaimsData(response.data)
        setTotal(response.total || response.data.length)
        setFilteredData(response.data) // Since filtering is done server-side
      } else {
        setClaimsData([])
        setFilteredData([])
        setTotal(0)
      }
    } catch (error) {
      console.error('Failed to load claims data:', error)
      message.error('Failed to load claims data')
      setClaimsData([])
      setFilteredData([])
    } finally {
      setLoading(false)
    }
  }

  const loadDashboardStats = async () => {
    try {
      const stats = await getClaimsStatsByType()

      // Process stats by type
      const statsByType = {
        COID: { count: 0, amount: 0, pending: 0, approved: 0 },
        FEM: { count: 0, amount: 0, pending: 0, approved: 0 },
        SAPS: { count: 0, amount: 0, pending: 0, approved: 0 },
        RMA: { count: 0, amount: 0, pending: 0, approved: 0 }
      }

      let totalClaims = 0
      let totalAmount = 0
      let pendingClaims = 0
      let approvedClaims = 0

      stats.forEach((typeStat) => {
        const type = typeStat._id
        if (statsByType[type]) {
          statsByType[type].count = typeStat.totalClaims
          statsByType[type].amount = typeStat.totalAmount

          typeStat.statusBreakdown.forEach((status) => {
            if (status.status === 'under_review' || status.status === 'submitted') {
              statsByType[type].pending += status.count
              pendingClaims += status.count
            } else if (status.status === 'approved' || status.status === 'paid') {
              statsByType[type].approved += status.count
              approvedClaims += status.count
            }
          })

          totalClaims += typeStat.totalClaims
          totalAmount += typeStat.totalAmount
        }
      })

      setDashboardStats({
        totalClaims,
        totalAmount,
        pendingClaims,
        approvedClaims,
        coidClaims: statsByType.COID.count,
        femClaims: statsByType.FEM.count,
        sapsClaims: statsByType.SAPS.count,
        rmaClaims: statsByType.RMA.count
      })
    } catch (error) {
      console.error('Failed to load dashboard stats:', error)
      message.error('Failed to load dashboard statistics')
    }
  }

  const filterData = useCallback(() => {
    let filtered = [...claimsData]

    // Text search
    if (searchText) {
      filtered = filtered.filter(
        (item) =>
          item.patientName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.claimNumber.toLowerCase().includes(searchText.toLowerCase()) ||
          item.fileNo?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.employerName?.toLowerCase().includes(searchText.toLowerCase())
      )
    }

    // Claim type filter
    if (filterClaimType !== 'all') {
      filtered = filtered.filter((item) => item.claimType === filterClaimType)
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((item) => item.status === filterStatus)
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.dateSubmitted)
        return itemDate.isAfter(dateRange[0]) && itemDate.isBefore(dateRange[1])
      })
    }

    setFilteredData(filtered)
  }, [claimsData, searchText, filterClaimType, filterStatus, filterAssignee, dateRange])

  const handleStatusChange = async (record, newStatus) => {
    try {
      await updateClaimStatus(record._id, newStatus, 'Status updated via compensation fund manager')
      message.success(`Status updated to ${newStatus}`)
      loadClaimsData()
      loadDashboardStats()
    } catch (error) {
      console.error('Failed to update status:', error)
      message.error('Failed to update status')
    }
  }

  const handleCreateClaim = async (values) => {
    try {
      const claimData = {
        ...values,
        dateOfInjury: values.dateOfInjury.format('YYYY-MM-DD'),
        amount: parseFloat(values.amount)
      }

      await createCompensationClaim(claimData)
      message.success('Claim created successfully')
      setClaimModalVisible(false)
      form.resetFields()
      loadClaimsData()
      loadDashboardStats()
    } catch (error) {
      console.error('Failed to create claim:', error)
      message.error('Failed to create claim')
    }
  }

  const handleUpdateClaim = async (values) => {
    try {
      const updateData = {
        ...values,
        dateOfInjury: values.dateOfInjury.format('YYYY-MM-DD'),
        amount: parseFloat(values.amount)
      }

      await updateCompensationClaim(editingClaim._id, updateData)
      message.success('Claim updated successfully')
      setClaimModalVisible(false)
      setEditingClaim(null)
      form.resetFields()
      loadClaimsData()
      loadDashboardStats()
    } catch (error) {
      console.error('Failed to update claim:', error)
      message.error('Failed to update claim')
    }
  }

  const handleDeleteClaim = async (claimId) => {
    try {
      await deleteCompensationClaim(claimId)
      message.success('Claim deleted successfully')
      loadClaimsData()
      loadDashboardStats()
    } catch (error) {
      console.error('Failed to delete claim:', error)
      message.error('Failed to delete claim')
    }
  }

  const handleUploadDocument = async (info) => {
    if (info.file.status === 'done') {
      try {
        const newDocument = {
          name: info.file.name,
          type: info.file.name.split('.').pop(),
          size: info.file.size,
          url: info.file.response?.url || '#'
        }

        await addClaimDocument(selectedClaim._id, newDocument)
        message.success(`${info.file.name} uploaded successfully`)

        // Refresh the selected claim
        const updatedClaims = await getCompensationClaims({ _id: selectedClaim._id }, 1, 1)
        if (updatedClaims.data && updatedClaims.data.length > 0) {
          setSelectedClaim(updatedClaims.data[0])
        }
      } catch (error) {
        console.error('Failed to add document:', error)
        message.error('Failed to add document to claim')
      }
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} upload failed`)
    }
  }

  const claimTypes = [
    {
      value: 'COID',
      label: 'COID - Compensation for Occupational Injuries and Diseases',
      icon: <FontAwesomeIcon icon={faHardHat} />,
      color: '#1890ff'
    },
    {
      value: 'FEM',
      label: 'FEM - Federated Employers Mutual',
      icon: <FontAwesomeIcon icon={faMedkit} />,
      color: '#52c41a'
    },
    {
      value: 'SAPS',
      label: 'SAPS - South African Police Service',
      icon: <FontAwesomeIcon icon={faShieldAlt} />,
      color: '#722ed1'
    },
    {
      value: 'RMA',
      label: 'RMA - Road Accident Fund',
      icon: <FontAwesomeIcon icon={faBalanceScale} />,
      color: '#fa8c16'
    }
  ]

  const statusOptions = [
    { value: 'Submitted', label: 'Submitted', color: 'blue' },
    { value: 'Under Review', label: 'Under Review', color: 'orange' },
    { value: 'Approved', label: 'Approved', color: 'green' },
    { value: 'Rejected', label: 'Rejected', color: 'red' },
    { value: 'Pending Documentation', label: 'Pending Documentation', color: 'purple' },
    { value: 'Payment Pending', label: 'Payment Pending', color: 'cyan' },
    { value: 'Completed', label: 'Completed', color: 'green' }
  ]

  const getClaimTypeIcon = (claimType) => {
    const type = claimTypes.find((t) => t.value === claimType)
    return type ? type.icon : <FontAwesomeIcon icon={faFileAlt} />
  }

  const getClaimTypeColor = (claimType) => {
    const type = claimTypes.find((t) => t.value === claimType)
    return type ? type.color : '#8c8c8c'
  }

  const columns = [
    {
      title: 'Patient',
      dataIndex: 'patientName',
      key: 'patientName',
      width: 180,
      fixed: 'left',
      render: (text, record) => (
        <div className='patient-cell'>
          <Avatar icon={<UserOutlined />} size='small' />
          <div className='patient-info'>
            <div className='patient-name'>{text}</div>
            <div className='patient-details'>
              <Text type='secondary' style={{ fontSize: '12px' }}>
                {record.billingId}
              </Text>
              {record.fileNo && (
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  • {record.fileNo}
                </Text>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Claim Type',
      dataIndex: 'claimType',
      key: 'claimType',
      width: 120,
      render: (claimType) => (
        <Tag color={getClaimTypeColor(claimType)} icon={getClaimTypeIcon(claimType)} style={{ fontWeight: 'bold' }}>
          {claimType}
        </Tag>
      ),
      filters: claimTypes.map((type) => ({
        text: type.value,
        value: type.value
      })),
      onFilter: (value, record) => record.claimType === value
    },
    {
      title: 'Claim Number',
      dataIndex: 'claimNumber',
      key: 'claimNumber',
      width: 150,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(amount)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
      align: 'right'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status, record) => (
        <Select
          value={status}
          onChange={(newStatus) => handleStatusChange(record, newStatus)}
          style={{ width: '100%' }}
          size='small'
        >
          {statusOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              <Tag color={option.color} style={{ margin: 0 }}>
                {option.label}
              </Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Date of Injury',
      dataIndex: 'dateOfInjury',
      key: 'dateOfInjury',
      width: 120,
      render: (date) => formatDate(date, 'DD MMM YYYY'),
      sorter: (a, b) => dayjs(a.dateOfInjury).unix() - dayjs(b.dateOfInjury).unix()
    },
    {
      title: 'Date Submitted',
      dataIndex: 'dateSubmitted',
      key: 'dateSubmitted',
      width: 120,
      render: (date) => formatDate(date, 'DD MMM YYYY'),
      sorter: (a, b) => dayjs(a.dateSubmitted).unix() - dayjs(b.dateSubmitted).unix()
    },
    {
      title: 'Employer',
      dataIndex: 'employerName',
      key: 'employerName',
      width: 150,
      ellipsis: true,
      render: (text) => <Tooltip title={text}>{text}</Tooltip>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Tooltip title='View Details'>
            <Button
              type='text'
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedClaim(record)
                setDetailsDrawerVisible(true)
              }}
            />
          </Tooltip>
          <Tooltip title='Edit'>
            <Button
              type='text'
              icon={<EditOutlined />}
              onClick={() => {
                setEditingClaim(record)
                setClaimModalVisible(true)
                form.setFieldsValue({
                  ...record,
                  dateOfInjury: dayjs(record.dateOfInjury)
                })
              }}
            />
          </Tooltip>
          <Tooltip title='Upload Document'>
            <Button
              type='text'
              icon={<UploadOutlined />}
              onClick={() => {
                setSelectedClaim(record)
                setDocumentModalVisible(true)
              }}
            />
          </Tooltip>
          <Popconfirm
            title='Are you sure you want to delete this claim?'
            onConfirm={() => handleDeleteClaim(record._id)}
            okText='Yes'
            cancelText='No'
          >
            <Tooltip title='Delete'>
              <Button type='text' icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      name: record._id
    })
  }

  return (
    <div className='compensation-fund-manager'>
      {/* Actions Bar */}
      <Row justify='end' style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingClaim(null)
              setClaimModalVisible(true)
              form.resetFields()
            }}
          >
            New Claim
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadClaimsData} loading={loading}>
            Refresh
          </Button>
          <Button icon={<ExportOutlined />} onClick={() => message.success('Export functionality coming soon')}>
            Export
          </Button>
        </Space>
      </Row>

      {/* Dashboard Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Total Claims'
              value={dashboardStats.totalClaims}
              prefix={<FontAwesomeIcon icon={faFileAlt} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Pending Claims'
              value={dashboardStats.pendingClaims}
              prefix={<FontAwesomeIcon icon={faClock} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Approved Claims'
              value={dashboardStats.approvedClaims}
              prefix={<FontAwesomeIcon icon={faCheckCircle} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Total Amount'
              value={dashboardStats.totalAmount}
              formatter={formatCurrency}
              prefix={<FontAwesomeIcon icon={faMoneyBillWave} />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Claim Type Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {claimTypes.map((type) => (
          <Col xs={24} sm={6} key={type.value}>
            <Card size='small'>
              <Statistic
                title={type.value}
                value={dashboardStats[`${type.value.toLowerCase()}Claims`] || 0}
                prefix={<span style={{ color: type.color }}>{type.icon}</span>}
                valueStyle={{ color: type.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters - No Card wrapper */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Input
            placeholder='Search claims, patients, employers...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={4}>
          <Select
            placeholder='Claim Type'
            value={filterClaimType}
            onChange={setFilterClaimType}
            style={{ width: '100%' }}
            allowClear
          >
            <Option value='all'>All Types</Option>
            {claimTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.value}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={4}>
          <Select
            placeholder='Status'
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: '100%' }}
            allowClear
          >
            <Option value='all'>All Status</Option>
            {statusOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: '100%' }}
            placeholder={['Start Date', 'End Date']}
          />
        </Col>
        <Col xs={24} sm={4}>
          <Text type='secondary'>
            Showing {filteredData.length} of {total} claims
          </Text>
        </Col>
      </Row>

      {/* Main Table - No Card wrapper */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowSelection={rowSelection}
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} claims`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => {
            setPage(page)
            setPageSize(pageSize)
          }
        }}
        rowKey='_id'
        size='small'
        style={{
          background: '#fff',
          borderRadius: '8px'
        }}
      />

      {/* Claim Modal */}
      <Modal
        title={editingClaim ? 'Edit Claim' : 'New Claim'}
        visible={claimModalVisible}
        onCancel={() => setClaimModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={form} layout='vertical' onFinish={editingClaim ? handleUpdateClaim : handleCreateClaim}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='Patient Name'
                name='patientName'
                rules={[{ required: true, message: 'Please enter patient name' }]}
              >
                <Input placeholder='Patient full name' />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='Claim Type'
                name='claimType'
                rules={[{ required: true, message: 'Please select claim type' }]}
              >
                <Select placeholder='Select claim type'>
                  {claimTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='File Number' name='fileNo'>
                <Input placeholder='External file reference (optional)' />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='Date of Injury'
                name='dateOfInjury'
                rules={[{ required: true, message: 'Please select date of injury' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Claim Amount'
                name='amount'
                rules={[{ required: true, message: 'Please enter claim amount' }]}
              >
                <Input type='number' step='0.01' prefix='R' placeholder='0.00' />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='Employer Name'
                name='employerName'
                rules={[{ required: true, message: 'Please enter employer name' }]}
              >
                <Input placeholder='Employer name' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='Employer Reference' name='employerReference'>
                <Input placeholder='Employer reference number' />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label='Injury Description'
            name='injuryDescription'
            rules={[{ required: true, message: 'Please describe the injury' }]}
          >
            <TextArea rows={3} placeholder='Describe the injury or condition...' />
          </Form.Item>
          <Form.Item
            label='Treatment Type'
            name='treatmentType'
            rules={[{ required: true, message: 'Please describe the treatment' }]}
          >
            <TextArea rows={3} placeholder='Describe the treatment provided...' />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type='primary' htmlType='submit'>
                {editingClaim ? 'Update Claim' : 'Create Claim'}
              </Button>
              <Button onClick={() => setClaimModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Drawer */}
      <Drawer
        title='Claim Details'
        placement='right'
        onClose={() => setDetailsDrawerVisible(false)}
        visible={detailsDrawerVisible}
        width={700}
      >
        {selectedClaim && (
          <div className='claim-details'>
            <Card title='Claim Information' style={{ marginBottom: 16 }}>
              <Descriptions column={2} size='small'>
                <Descriptions.Item label='Claim Number'>{selectedClaim.claimNumber}</Descriptions.Item>
                <Descriptions.Item label='Claim Type'>
                  <Tag color={getClaimTypeColor(selectedClaim.claimType)}>{selectedClaim.claimType}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label='Status'>
                  <Badge color={getStatusColor(selectedClaim.status.toLowerCase())} text={selectedClaim.status} />
                </Descriptions.Item>
                <Descriptions.Item label='Amount'>
                  <Text strong>{formatCurrency(selectedClaim.amount)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Date of Injury'>{formatDate(selectedClaim.dateOfInjury)}</Descriptions.Item>
                <Descriptions.Item label='Date Submitted'>{formatDate(selectedClaim.dateSubmitted)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title='Patient Information' style={{ marginBottom: 16 }}>
              <Descriptions column={2} size='small'>
                <Descriptions.Item label='Patient Name'>{selectedClaim.patientName}</Descriptions.Item>
                <Descriptions.Item label='Account Number'>{selectedClaim.billingId}</Descriptions.Item>
                <Descriptions.Item label='File Number'>{selectedClaim.fileNo}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title='Employer Information' style={{ marginBottom: 16 }}>
              <Descriptions column={2} size='small'>
                <Descriptions.Item label='Employer Name'>{selectedClaim.employerName}</Descriptions.Item>
                <Descriptions.Item label='Employer Reference'>{selectedClaim.employerReference}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title='Injury & Treatment' style={{ marginBottom: 16 }}>
              <Descriptions column={1} size='small'>
                <Descriptions.Item label='Injury Description'>{selectedClaim.injuryDescription}</Descriptions.Item>
                <Descriptions.Item label='Treatment Type'>{selectedClaim.treatmentType}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title='Documents' style={{ marginBottom: 16 }}>
              <List
                dataSource={selectedClaim.documents}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button type='link' icon={<DownloadOutlined />}>
                        Download
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar icon={<FileTextOutlined />} />}
                      title={item.name}
                      description={`${item.type.toUpperCase()} • ${formatDate(item.uploadDate)}`}
                    />
                  </List.Item>
                )}
              />
              <Button
                type='dashed'
                icon={<UploadOutlined />}
                onClick={() => setDocumentModalVisible(true)}
                style={{ width: '100%', marginTop: 16 }}
              >
                Upload Document
              </Button>
            </Card>

            <Card title='Follow-up History'>
              <Timeline>
                {selectedClaim.followUpHistory?.map((item, index) => (
                  <Timeline.Item key={index}>
                    <div>
                      <Text strong>{item.action}</Text>
                      <br />
                      <Text type='secondary'>
                        {formatDate(item.date)} - {item.user}
                      </Text>
                      {item.notes && (
                        <>
                          <br />
                          <Text>{item.notes}</Text>
                        </>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          </div>
        )}
      </Drawer>

      {/* Document Upload Modal */}
      <Modal
        title='Upload Document'
        visible={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        footer={null}
        width={500}
      >
        <Upload.Dragger
          name='file'
          multiple={false}
          action='/api/upload'
          onChange={handleUploadDocument}
          accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
        >
          <p className='ant-upload-drag-icon'>
            <UploadOutlined />
          </p>
          <p className='ant-upload-text'>Click or drag file to this area to upload</p>
          <p className='ant-upload-hint'>Support for PDF, DOC, DOCX, JPG, JPEG, PNG files</p>
        </Upload.Dragger>
      </Modal>
    </div>
  )
}

export default CompensationFundManager
